<template>
  <div class="ski-log-create-container">
    <LoadingSpinner v-if="loading" />
    <template v-else>
      <!-- Header -->
      <div class="header">
        <button @click="goBack" class="back-button">
          ← Back to Logs
        </button>
        <h1>New Ski Session</h1>
      </div>

      <!-- Step Indicator -->
      <div class="step-indicator">
        <div 
          v-for="(step, index) in steps" 
          :key="index"
          class="step-dot"
          :class="{
            'active': index + 1 === currentStep,
            'completed': index + 1 < currentStep
          }"
        >
          <span v-if="index + 1 < currentStep">✓</span>
          <span v-else>{{ index + 1 }}</span>
        </div>
      </div>

      <!-- Step Content -->
      <div class="step-content">
        <!-- Step 1: Basic Info -->
        <div v-if="currentStep === 1" class="step-card">
          <h2>{{ steps[0] }}</h2>
          <div class="form-grid">
            <div class="form-group">
              <label for="date">Date</label>
              <input
                type="date"
                id="date"
                v-model="log.date"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="lake">Lake/Location</label>
              <input
                type="text"
                id="lake"
                v-model="log.lake"
                placeholder="Lake name or location"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="driver">Driver</label>
              <input
                type="text"
                id="driver"
                v-model="log.driver"
                placeholder="Who was driving"
                class="form-input"
              />
            </div>
          </div>
        </div>

        <!-- Step 2: Equipment -->
        <div v-if="currentStep === 2" class="step-card">
          <h2>{{ steps[1] }}</h2>
          <div class="form-grid">
            <div class="form-group">
              <label for="boat">Boat</label>
              <select id="boat" v-model="log.boat" class="form-input">
                <option value="">Select boat...</option>
                <option v-for="(label, value) in boatChoices" :key="value" :value="value">
                  {{ label }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label for="boat_type">Boat Model</label>
              <input
                type="text"
                id="boat_type"
                v-model="log.boat_type"
                placeholder="e.g., 2023 G23"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="ski">Ski</label>
              <select id="ski" v-model="log.ski" class="form-input">
                <option value="">Select ski...</option>
                <option v-for="(label, value) in skiChoices" :key="value" :value="value">
                  {{ label }}
                </option>
              </select>
            </div>
            <div class="form-group">
              <label for="ski_type">Ski Model</label>
              <input
                type="text"
                id="ski_type"
                v-model="log.ski_type"
                placeholder="e.g., Radar Senate"
                class="form-input"
              />
            </div>
          </div>
        </div>

        <!-- Step 3: Conditions -->
        <div v-if="currentStep === 3" class="step-card">
          <h2>{{ steps[2] }}</h2>
          <div class="form-grid">
            <div class="form-group">
              <label for="conditions">Conditions (%)</label>
              <input
                type="number"
                id="conditions"
                v-model="log.conditions"
                min="0"
                max="100"
                placeholder="0-100"
                class="form-input"
              />
            </div>
            <div class="form-group full-width">
              <label for="conditions_detail">Conditions Details</label>
              <textarea
                id="conditions_detail"
                v-model="log.conditions_detail"
                placeholder="Additional details about water conditions..."
                class="form-textarea"
                rows="3"
              ></textarea>
            </div>
          </div>
        </div>

        <!-- Step 4: Passes -->
        <div v-if="currentStep === 4" class="step-card">
          <h2>{{ steps[3] }}</h2>
          <div class="passes-section">
            <div class="passes-mobile">
              <div 
                v-for="(pass, index) in passes" 
                :key="pass.id || `new-${index}`"
                class="pass-card"
              >
                <div class="pass-header">
                  <h4>Pass {{ index + 1 }}</h4>
                  <button
                    @click="deletePass(pass, index)"
                    class="delete-btn"
                    title="Delete pass"
                  >
                    ×
                  </button>
                </div>
                
                <div class="pass-form">
                  <div class="form-group">
                    <label>Speed</label>
                    <select v-model="pass.boat_speed" class="form-input">
                      <option value="">Select...</option>
                      <option v-for="speed in sortedSpeedChoices" :key="speed.value" :value="speed.value">
                        {{ speed.label }}
                      </option>
                    </select>
                  </div>
                  <div class="form-group">
                    <label>Rope Length</label>
                    <select v-model="pass.rope_length" class="form-input">
                      <option value="">Select...</option>
                      <option v-for="(label, value) in ropeChoices" :key="value" :value="value">
                        {{ label }}
                      </option>
                    </select>
                  </div>
                  <div class="form-group">
                    <label>Buoys</label>
                    <input
                      type="number"
                      v-model="pass.buoys"
                      class="form-input"
                      min="0"
                      max="6"
                    />
                  </div>
                </div>
              </div>
              
              <!-- Add Pass Button after all passes -->
              <button @click="addPass" class="action-button primary add-pass-btn">
                + Add Pass
              </button>
            </div>
          </div>
        </div>

        <!-- Step 5: Ski Settings -->
        <div v-if="currentStep === 5" class="step-card">
          <h2>{{ steps[4] }}</h2>
          <p class="step-description">Optional: Update your ski settings for this session</p>
          
          <div v-if="loadingSkiSettings" class="loading-message">
            Loading ski settings...
          </div>
          
          <div v-else class="form-grid">
            <div class="form-group">
              <label for="boot_placement">Boot Placement</label>
              <input
                type="text"
                id="boot_placement"
                v-model="skiSettings.boot_placement"
                placeholder="e.g., Forward 2cm"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="fin_length">Fin Length</label>
              <input
                type="text"
                id="fin_length"
                v-model="skiSettings.fin_length"
                placeholder="e.g., 6.5 inches"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="fin_depth">Fin Depth</label>
              <input
                type="text"
                id="fin_depth"
                v-model="skiSettings.fin_depth"
                placeholder="e.g., 1.2 inches"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="fin_dft">Fin DFT</label>
              <input
                type="text"
                id="fin_dft"
                v-model="skiSettings.fin_dft"
                placeholder="e.g., 0.8 inches"
                class="form-input"
              />
            </div>
            <div class="form-group">
              <label for="wing_degree">Wing Degree</label>
              <input
                type="text"
                id="wing_degree"
                v-model="skiSettings.wing_degree"
                placeholder="e.g., 2 degrees"
                class="form-input"
              />
            </div>
          </div>
        </div>

        <!-- Step 6: Feedback -->
        <div v-if="currentStep === 6" class="step-card">
          <h2>{{ steps[5] }}</h2>
          <div class="feedback-grid">
            <div class="form-group">
              <label for="positive">What went well?</label>
              <textarea
                id="positive"
                v-model="log.positive"
                placeholder="Things that went well during this session..."
                class="form-textarea"
                rows="3"
              ></textarea>
            </div>
            <div class="form-group">
              <label for="negative">What didn't go well?</label>
              <textarea
                id="negative"
                v-model="log.negative"
                placeholder="Things that could have gone better..."
                class="form-textarea"
                rows="3"
              ></textarea>
            </div>
            <div class="form-group">
              <label for="improve">What to improve?</label>
              <textarea
                id="improve"
                v-model="log.improve"
                placeholder="Areas to focus on improving..."
                class="form-textarea"
                rows="3"
              ></textarea>
            </div>
            <div class="form-group">
              <label for="try_next">What to try next time?</label>
              <textarea
                id="try_next"
                v-model="log.try_next"
                placeholder="Things to try in the next session..."
                class="form-textarea"
                rows="3"
              ></textarea>
            </div>
          </div>
        </div>
      </div>

      <!-- Navigation -->
      <div class="navigation">
        <button 
          v-if="currentStep > 1" 
          @click="previousStep" 
          class="action-button secondary"
        >
          Previous
        </button>
        <div class="spacer"></div>
        <button 
          v-if="currentStep < totalSteps" 
          @click="nextStep" 
          class="action-button primary"
          :disabled="!canProceed"
        >
          Next
        </button>
        <button 
          v-if="currentStep === totalSteps" 
          @click="saveLog" 
          class="action-button primary"
          :disabled="saving"
        >
          {{ saving ? 'Creating...' : 'Create Log' }}
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import { API_BASE_URL } from '@/config';
import axios from 'axios';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

export default {
  name: 'SkiLogCreate',
  components: {
    LoadingSpinner
  },
  data() {
    return {
      currentStep: 1,
      totalSteps: 6,
      steps: [
        'Basic Information',
        'Equipment',
        'Water Conditions',
        'Passes',
        'Ski Settings',
        'Session Feedback'
      ],
      log: {
        date: new Date().toISOString().split('T')[0],
        lake: '',
        boat: '',
        boat_type: '',
        ski: '',
        ski_type: '',
        driver: '',
        conditions: '',
        conditions_detail: '',
        positive: '',
        negative: '',
        improve: '',
        try_next: ''
      },
      passes: [],
      skiSettings: {
        boot_placement: '',
        fin_length: '',
        fin_depth: '',
        fin_dft: '',
        wing_degree: ''
      },
      loading: false,
      saving: false,
      loadingSkiSettings: false,
      boatChoices: {
        'nautique': 'Nautique',
        'mastercraft': 'MasterCraft',
        'malibu': 'Malibu',
        'centurion': 'Centurion',
        'other': 'Other',
      },
      skiChoices: {
        'connelly': 'Connelly',
        'd3': 'D3',
        'goode': 'Goode',
        'ho': 'HO',
        'kd': 'KD',
        'radar': 'Radar',
        'reflex': 'Reflex',
        'obrien': 'Obrien',
        'denali': 'Denali',
        'pineapple': 'Pineapple',
        'lapoint': 'Lapoint',
        'other': 'Other',
      },
      speedChoices: {
        '58': '58kph - 36mph',
        '57': '57kph - 35mph',
        '56': '56kph - 35mph',
        '55': '55kph - 34mph',
        '54': '54kph - 34mph',
        '53': '53kph - 33mph',
        '52': '52kph - 32mph',
        '51': '51kph - 32mph',
        '50': '50kph - 31mph',
        '49': '49kph - 30mph',
        '48': '48kph - 30mph',
        '47': '47kph - 29mph',
        '46': '46kph - 28mph',
        '45': '45kph - 28mph',
        '44': '44kph - 27mph',
        '43': '43kph - 26mph',
        '42': '42kph - 26mph',
        '41': '41kph - 25mph',
        '40': '40kph - 25mph',
        '39': '39kph - 24mph',
        '38': '38kph - 24mph',
        '37': '37kph - 23mph',
      },
      ropeChoices: {
        '18.25': '18.25 meter - 15 Off',
        '16.00': '16.00 meter - 22 Off',
        '14.25': '14.25 meter - 28 Off',
        '13.00': '13.00 meter - 32 Off',
        '12.00': '12.00 meter - 35 Off',
        '11.25': '11.25 meter - 38 Off',
        '10.75': '10.75 meter - 39.5 Off',
        '10.25': '10.25 meter - 41 Off',
        '9.75': '9.75 meter - 43 Off',
      }
    };
  },
  computed: {
    sortedSpeedChoices() {
      return Object.entries(this.speedChoices)
        .sort((a, b) => parseInt(b[0]) - parseInt(a[0]))
        .map(([value, label]) => ({ value, label }));
    },
    canProceed() {
      // Basic validation for each step
      switch (this.currentStep) {
        case 1:
          return this.log.date; // Date is required
        case 2:
          return true; // Equipment is optional
        case 3:
          return true; // Conditions are optional
        case 4:
          return true; // Passes are optional
        case 5:
          return true; // Ski settings are optional
        case 6:
          return true; // Feedback is optional
        default:
          return true;
      }
    }
  },
  async mounted() {
    // Automatically add the first pass
    this.addPass();
    // Fetch current ski settings
    await this.fetchSkiSettings();
  },
  methods: {
    nextStep() {
      if (this.currentStep < this.totalSteps && this.canProceed) {
        this.currentStep++;
      }
    },

    previousStep() {
      if (this.currentStep > 1) {
        this.currentStep--;
      }
    },

    addPass() {
      const newPass = {
        pass_number: this.passes.length + 1,
        boat_speed: '',
        rope_length: '',
        buoys: '',
        isNew: true
      };
      this.passes.push(newPass);
    },

    deletePass(pass, index) {
      this.passes.splice(index, 1);
    },

    async fetchSkiSettings() {
      this.loadingSkiSettings = true;
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(`${API_BASE_URL}/api/ski-settings/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        this.skiSettings = {
          boot_placement: response.data.boot_placement || '',
          fin_length: response.data.fin_length || '',
          fin_depth: response.data.fin_depth || '',
          fin_dft: response.data.fin_dft || '',
          wing_degree: response.data.wing_degree || ''
        };
      } catch (error) {
        console.error('Error fetching ski settings:', error);
        // If settings don't exist, that's okay
      } finally {
        this.loadingSkiSettings = false;
      }
    },

    async saveLog() {
      this.saving = true;
      try {
        const token = localStorage.getItem('access_token');
        
        // Step 1: Create the log
        const logResponse = await axios.post(`${API_BASE_URL}/skilog/logs/create/`, this.log, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        // Step 2: Get the new log ID
        const newLogId = logResponse.data.id;
        
        // Step 3: Create each pass that has data
        for (const pass of this.passes) {
          const hasData = pass.pass_number || pass.boat_speed || pass.rope_length || pass.buoys;
          if (hasData) {
            const passData = {
              log: newLogId,
              pass_number: pass.pass_number || null,
              boat_speed: pass.boat_speed || null,
              rope_length: pass.rope_length ? parseFloat(pass.rope_length) : null,
              buoys: pass.buoys || null
            };
            
            try {
              await axios.post(`${API_BASE_URL}/skilog/logs/${newLogId}/passes/create/`, passData, {
                headers: { Authorization: `Bearer ${token}` },
              });
            } catch (passError) {
              console.error('Error creating pass:', passError);
            }
          }
        }
        
        // Step 4: Update ski settings if changed
        try {
          await axios.patch(`${API_BASE_URL}/api/ski-settings/`, this.skiSettings, {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
          });
        } catch (settingsError) {
          console.error('Error updating ski settings:', settingsError);
        }
        
        // Step 5: Redirect to log list
        this.$router.push({ name: 'SkiLog' });
      } catch (error) {
        console.error('Error saving log:', error);
        alert('Error creating log. Please try again.');
      } finally {
        this.saving = false;
      }
    },

    goBack() {
      this.$router.push({ name: 'SkiLog' });
    }
  }
};
</script>

<style scoped>
.ski-log-create-container {
  max-width: 600px;
  margin: 0 auto;
  margin-bottom: 80px;
  padding: 20px;
  background-color: #15202b;
  min-height: 100vh;
}

.header {
  text-align: center;
  margin-bottom: 30px;
}

.header h1 {
  color: #ffffff;
  margin: 16px 0 0 0;
  font-size: 1.8rem;
}

.back-button {
  background-color: #8899a6;
  color: white;
  border: none;
  padding: 10px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  margin-bottom: 16px;
}

.back-button:hover {
  background-color: #7a8a97;
}

.step-indicator {
  display: flex;
  justify-content: center;
  gap: 16px;
  margin-bottom: 32px;
}

.step-dot {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: #38444d;
  color: #8899a6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.step-dot.active {
  background-color: #1da1f2;
  color: #ffffff;
}

.step-dot.completed {
  background-color: #2ecc71;
  color: #ffffff;
}

.step-content {
  margin-bottom: 32px;
}

.step-card {
  background-color: #192734;
  border-radius: 16px;
  padding: 24px;
}

.step-card h2 {
  color: #ffffff;
  margin: 0 0 8px 0;
  font-size: 1.4rem;
}

.step-description {
  color: #8899a6;
  margin: 0 0 24px 0;
  font-style: italic;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  color: #ffffff;
  margin-bottom: 8px;
  font-weight: 500;
}

.form-input, .form-textarea {
  padding: 12px 16px;
  border: 1px solid #38444d;
  border-radius: 8px;
  background-color: #15202b;
  color: #ffffff;
  font-size: 16px;
  box-sizing: border-box;
}

.form-input:focus, .form-textarea:focus {
  outline: none;
  border-color: #1da1f2;
}

.form-textarea {
  resize: vertical;
  font-family: inherit;
}

.feedback-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
}

.passes-section {
  margin-top: 8px;
}

.passes-mobile {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.add-pass-btn {
  margin-top: 8px;
  width: 100%;
}

.pass-card {
  background-color: #15202b;
  border: 1px solid #38444d;
  border-radius: 12px;
  padding: 16px;
}

.pass-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.pass-header h4 {
  color: #ffffff;
  margin: 0;
  font-size: 1.1rem;
}

.pass-form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.pass-form .form-group:last-child {
  grid-column: 1 / -1;
}

.delete-btn {
  background-color: #e1306c;
  color: white;
  border: none;
  border-radius: 4px;
  width: 28px;
  height: 28px;
  cursor: pointer;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.delete-btn:hover {
  background-color: #c92a5d;
}

.loading-message {
  color: #8899a6;
  font-style: italic;
  text-align: center;
  padding: 20px;
}

.navigation {
  display: flex;
  gap: 16px;
  align-items: center;
  padding: 20px 0;
}

.spacer {
  flex: 1;
}

.action-button {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-button.primary {
  background-color: #1da1f2;
  color: white;
}

.action-button.primary:hover {
  background-color: #1991db;
}

.action-button.primary:disabled {
  background-color: #38444d;
  color: #657786;
  cursor: not-allowed;
}

.action-button.secondary {
  background-color: #38444d;
  color: #ffffff;
}

.action-button.secondary:hover {
  background-color: #4a5a67;
}

.action-button.small {
  padding: 8px 16px;
  font-size: 14px;
}

@media screen and (min-width: 600px) {
  .form-grid {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  }
  
  .feedback-grid {
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  }
}

@media screen and (max-width: 480px) {
  .ski-log-create-container {
    padding: 16px;
  }
  
  .step-indicator {
    gap: 8px;
  }
  
  .step-dot {
    width: 28px;
    height: 28px;
    font-size: 12px;
  }
  
  .pass-form {
    grid-template-columns: 1fr;
  }
}
</style>
